import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
    FaBuilding, 
    FaUsers, 
    FaCheckCircle, 
    FaTimesCircle,
    FaSpinner,
    FaUserPlus,
    FaUserMinus,
    FaHistory,
    FaBars,
    FaRegCalendarAlt,
    FaExclamationTriangle,
    FaEye,
    FaTachometerAlt,
    FaSyncAlt,
    FaEdit,
    FaTrash,
    FaSearch
} from 'react-icons/fa';
import Topbar from '../layouts/Topbar';
import Sidebar from '../layouts/Sidebar';
import './Dashboard.css';

const Dashboard = () => {
    const baseUrl = "https://api.burrayhr.com";
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [statistics, setStatistics] = useState({
        totalCompanies: 0,
        activeCompanies: 0,
        inactiveCompanies: 0,
        totalAdminManagers: 0
    });
    const [recentActivities, setRecentActivities] = useState([]);
    const [allActivities, setAllActivities] = useState([]);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchDashboardData();
    }, []);

    // Filter activities based on search term
    useEffect(() => {
        if (searchTerm.trim() === '') {
            setRecentActivities(allActivities);
        } else {
            const lowercaseSearchTerm = searchTerm.toLowerCase();
            const filtered = allActivities.filter(activity => 
                (activity.company_name && activity.company_name.toLowerCase().includes(lowercaseSearchTerm)) || 
                (activity.user_email && activity.user_email.toLowerCase().includes(lowercaseSearchTerm))
            );
            setRecentActivities(filtered);
        }
    }, [searchTerm, allActivities]);

    const fetchDashboardData = async () => {
        setIsRefreshing(true);
        try {
            const token = localStorage.getItem('token');
            const headers = { 'Authorization': `Bearer ${token}` };

            // Fetch companies data
            const companiesResponse = await axios.get(`${baseUrl}/api/companies/`, { headers });
            
            // Calculate statistics
            const activeCompanies = companiesResponse.data.filter(company => company.is_active).length;
            const inactiveCompanies = companiesResponse.data.filter(company => !company.is_active).length;

            setStatistics({
                totalCompanies: companiesResponse.data.length,
                activeCompanies,
                inactiveCompanies,
                totalAdminManagers: companiesResponse.data.reduce((acc, company) => 
                    acc + (company.admin_managers?.length || 0), 0)
            });

            // Fetch recent activities
            const activitiesResponse = await axios.get(`${baseUrl}/api/activity-logs/`, { headers });
            console.log("Activities",activitiesResponse.data);
            
            // Filter activities by the allowed types
            const allowedActivityTypes = [
                'action_type',
                'company_create', 
                'company_update', 
                'company_deleted', 
                'company_activate_deactivate',
                'employee_create',
            ];
            const filteredActivities = activitiesResponse.data.filter(
                activity => allowedActivityTypes.includes(activity.action_type)
            );
            
            setAllActivities(filteredActivities);
            setRecentActivities(filteredActivities);
            setError(null);
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            setError('Failed to load dashboard data');
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    };

    const getFormattedDate = () => {
        const now = new Date();
        const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
        return now.toLocaleDateString('en-US', options);
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const clearSearch = () => {
        setSearchTerm('');
    };

    if (loading) {
        return (
            <>
            <Topbar />
            <Sidebar />
            <div className="full-page-loader">
                <div className="loader-content">
                    <div className="loader-icon">
                        <FaSpinner />
                    </div>
                    <h3>Loading Dashboard</h3>
                    <p>Please wait while we fetch your data</p>
                </div>
            </div>
            </>
        );
    }

    return (
        <div>
            <Topbar />
            <Sidebar />
            <div className="main-content">
                <div className="dashboard-container">
                    {/* Dashboard Header */}
                    <div className="dashboard-welcome">
                        <div className="welcome-text">
                            <h1>Dashboard</h1>
                            <p>Welcome to BurrayHR Solutions Admin Panel</p>
                            <span className="current-date">
                                <FaRegCalendarAlt /> {getFormattedDate()}
                            </span>
                        </div>
                        <div className="dashboard-actions">
                            <button 
                                className={`refresh-btn ${isRefreshing ? 'refreshing' : ''}`} 
                                onClick={fetchDashboardData}
                                disabled={isRefreshing}
                            >
                                <FaSyncAlt /> {isRefreshing ? 'Refreshing...' : 'Refresh Data'}
                            </button>
                        </div>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="error-alert">
                            <FaExclamationTriangle />
                            <p>{error}</p>
                            <button onClick={fetchDashboardData}>Retry</button>
                        </div>
                    )}

                    {/* Stats Overview */}
                    <div className="stats-container">
                        <div className="stats-card companies-card">
                            <div className="stats-icon">
                                <FaBuilding />
                            </div>
                            <div className="stats-info">
                                <h3>Total Companies</h3>
                                <div className="stats-number">{statistics.totalCompanies}</div>
                                <div className="stats-progress">
                                    <div className="progress-bar" style={{ width: '100%' }}></div>
                                </div>
                            </div>
                        </div>

                        <div className="stats-card active-card">
                            <div className="stats-icon">
                                <FaCheckCircle />
                            </div>
                            <div className="stats-info">
                                <h3>Active Companies</h3>
                                <div className="stats-number">{statistics.activeCompanies}</div>
                                <div className="stats-progress">
                                    <div 
                                        className="progress-bar" 
                                        style={{ 
                                            width: `${statistics.totalCompanies ? 
                                                    (statistics.activeCompanies / statistics.totalCompanies) * 100 : 0}%` 
                                        }}
                                    ></div>
                                </div>
                            </div>
                        </div>

                        <div className="stats-card inactive-card">
                            <div className="stats-icon">
                                <FaTimesCircle />
                            </div>
                            <div className="stats-info">
                                <h3>Inactive Companies</h3>
                                <div className="stats-number">{statistics.inactiveCompanies}</div>
                                <div className="stats-progress">
                                    <div 
                                        className="progress-bar" 
                                        style={{ 
                                            width: `${statistics.totalCompanies ? 
                                                    (statistics.inactiveCompanies / statistics.totalCompanies) * 100 : 0}%` 
                                        }}
                                    ></div>
                                </div>
                            </div>
                        </div>

                    </div>

                    {/* Recent Activities Section */}
                    <div className="activities-section">
                        <div className="section-header">
                            <h2><FaHistory /> Company Activities</h2>
                            <div className="search-container">
                                <div className="search-input-wrapper">
                                    <FaSearch className="search-icon" />
                                    <input 
                                        type="text" 
                                        className="search-input" 
                                        placeholder="Search by company or user..." 
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                    />
                                    {searchTerm && (
                                        <button className="clear-search" onClick={clearSearch}>
                                            &times;
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="activities-content">
                            {recentActivities.length > 0 ? (
                                <div className="activity-list">
                                    {recentActivities.map((activity, index) => (
                                        <div key={index} className="activity-item">
                                            <div className={`activity-indicator ${getActivityColorClass(activity.action_type)}`}></div>
                                            <div className="activity-icon">
                                                {getActivityIcon(activity.action_type)}
                                            </div>
                                            <div className="activity-details">
                                                <div className="activity-header">
                                                    <h4 className="company-name">{activity.company_name}</h4>
                                                    <span className="activity-badge">
                                                        {formatActivityType(activity.action_type_display)}
                                                    </span>
                                                </div>
                                                <p className="activity-description">{activity.description}</p>
                                                <div className="activity-meta">
                                                    <span className="activity-user">
                                                        <FaUserPlus className="user-icon" /> {activity.user_email}
                                                    </span>
                                                    <span className="activity-time">
                                                        <FaRegCalendarAlt className="time-icon" /> {formatTimestamp(activity.created_at)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="empty-activities">
                                    <div className="empty-icon">
                                        <FaHistory />
                                    </div>
                                    <h3>{searchTerm ? 'No Matching Activities' : 'No Recent Activities'}</h3>
                                    <p>{searchTerm ? 'Try a different search term or clear the search.' : 'New activities will appear here as they occur.'}</p>
                                    {searchTerm && (
                                        <button className="clear-search-btn" onClick={clearSearch}>
                                            Clear Search
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Helper functions
const getActivityColorClass = (type) => {
    switch (type) {
        case 'company_create':
        case 'action_type':
            return 'indicator-blue';
        case 'company_update':
            return 'indicator-purple';
        case 'company_activate_deactivate':
            return 'indicator-green';
        case 'company_deleted':
            return 'indicator-red';
        default:
            return 'indicator-gray';
    }
};

const getActivityIcon = (type) => {
    switch (type) {
        case 'company_create':
            return <FaBuilding />;
        case 'company_update':
            return <FaEdit />;
        case 'company_activate_deactivate':
            return <FaCheckCircle />;
        case 'company_deleted':
            return <FaTrash />;
        case 'employee_create':
            return <FaUserPlus />;
        case 'action_type':
            return <FaHistory />;
        default:
            return <FaHistory />;
    }
};

const formatActivityType = (type) => {
    if (!type) return 'Activity';
    
    // Handle special cases
    switch(type) {
        case 'company_create':
            return 'New Company';
        case 'company_update':
            return 'Company Updated';
        case 'company_deleted':
            return 'Company Removed';
        case 'company_activate_deactivate':
            return 'Status Changed';
        case 'employee_create':
            return 'Employee Added';
        default:
            // Generic formatting for other types
            return type
                .split('_')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
    }
};

const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMin = Math.round(diffMs / 60000);
    const diffHrs = Math.round(diffMs / 3600000);
    const diffDays = Math.round(diffMs / 86400000);

    if (diffMin < 60) {
        return `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`;
    } else if (diffHrs < 24) {
        return `${diffHrs} hour${diffHrs !== 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
        return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    } else {
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    }
};

export default Dashboard;
